"use client";

import { useTransition } from "react";
import { useForm } from "react-hook-form";

import * as z from "zod";
import { requestVerificationCode } from "@/app/(portal)/(unauthenticated)/(forms)/actions";
import { PhoneLoginFormSchema } from "@/app/(portal)/(unauthenticated)/(forms)/schemas";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/components/ui/hooks/use-toast";
import { InputLabelAnimation as Input } from "@/components/ui/input-label-animation";
import { useLogin } from "@/lib/login/context";
import { zodResolver } from "@hookform/resolvers/zod";
import { withMask } from "use-mask-input";

export default function PhoneForm() {
  const { toast } = useToast();
  const [isPending, startTransition] = useTransition();

  const form = useForm<z.infer<typeof PhoneLoginFormSchema>>({
    resolver: zodResolver(PhoneLoginFormSchema),
    defaultValues: {
      phone_number: "",
    },
  });

  const { setVerification } = useLogin();

  const onSubmit = (data: z.infer<typeof PhoneLoginFormSchema>) => {
    startTransition(async () => {
      try {
        await requestVerificationCode("sms", data.phone_number);

        setVerification({
          method: "sms",
          value: data.phone_number,
        });
        toast({
          title: `A verification code has been sent to ${data.phone_number}`,
        });
      } catch (e) {
        console.error(e);
        toast({
          title: "There was an error sending the verification code",
          variant: "destructive",
        });
      }
    });
  };

  const phone_number = form.watch("phone_number");
  const isReady =
    PhoneLoginFormSchema.shape.phone_number.safeParse(phone_number).success;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex w-full flex-col gap-6"
      >
        <FormField
          control={form.control}
          name="phone_number"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  placeholder="Mobile Phone Number"
                  autoComplete="tel"
                  inputMode="tel"
                  schema={PhoneLoginFormSchema}
                  {...field}
                  ref={withMask("+1 (999) 999-9999", {
                    showMaskOnHover: false,
                  })}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className="h-12 w-full rounded-xl px-5 py-3 text-sm font-normal"
          variant={!isReady ? "outline" : "default"}
          disabled={isPending || !isReady}
        >
          Sign In with Phone
        </Button>
      </form>
    </Form>
  );
}
